/**
 * Validation Utilities
 * Form validation helpers
 */

import { VALIDATION } from '../config/constants'

/**
 * Validates email format
 */
export const validateEmail = (email) => {
  if (!email) {
    return { valid: false, error: 'Email is required' }
  }
  
  if (!VALIDATION.EMAIL_REGEX.test(email)) {
    return { valid: false, error: 'Please enter a valid email address' }
  }
  
  return { valid: true }
}

/**
 * Validates Google Drive link
 */
export const validateDriveLink = (link) => {
  if (!link) {
    return { valid: false, error: 'Google Drive link is required' }
  }
  
  if (!VALIDATION.GOOGLE_DRIVE_REGEX.test(link)) {
    return { valid: false, error: 'Please enter a valid Google Drive link' }
  }
  
  return { valid: true }
}

/**
 * Validates name
 */
export const validateName = (name) => {
  if (!name) {
    return { valid: false, error: 'Name is required' }
  }
  
  if (name.length < VALIDATION.MIN_NAME_LENGTH) {
    return { valid: false, error: `Name must be at least ${VALIDATION.MIN_NAME_LENGTH} characters` }
  }
  
  if (name.length > VALIDATION.MAX_NAME_LENGTH) {
    return { valid: false, error: `Name must be less than ${VALIDATION.MAX_NAME_LENGTH} characters` }
  }
  
  return { valid: true }
}

/**
 * Validates required field
 */
export const validateRequired = (value, fieldName = 'Field') => {
  if (!value || value.trim() === '') {
    return { valid: false, error: `${fieldName} is required` }
  }
  
  return { valid: true }
}

/**
 * Validates phone number (required)
 */
export const validatePhone = (phone) => {
  if (!phone || phone.trim() === '') {
    return { valid: false, error: 'Phone number is required' }
  }
  
  // Basic phone validation (allows +, digits, spaces, dashes, parentheses)
  // Minimum 10 digits for valid phone number
  const digitsOnly = phone.replace(/\D/g, '')
  if (digitsOnly.length < 10) {
    return { valid: false, error: 'Please enter a valid phone number (at least 10 digits)' }
  }
  
  const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/
  
  if (!phoneRegex.test(phone.trim())) {
    return { valid: false, error: 'Please enter a valid phone number' }
  }
  
  return { valid: true }
}

/**
 * Validates entire upload form
 */
export const validateUploadForm = (formData) => {
  const errors = {}
  
  const nameValidation = validateName(formData.name)
  if (!nameValidation.valid) {
    errors.name = nameValidation.error
  }
  
  const emailValidation = validateEmail(formData.email)
  if (!emailValidation.valid) {
    errors.email = emailValidation.error
  }
  
  // Phone is required
  const phoneValidation = validatePhone(formData.phone)
  if (!phoneValidation.valid) {
    errors.phone = phoneValidation.error
  }
  
  const driveLinkValidation = validateDriveLink(formData.driveLink)
  if (!driveLinkValidation.valid) {
    errors.driveLink = driveLinkValidation.error
  }
  
  const matchTypeValidation = validateRequired(formData.matchType, 'Match type')
  if (!matchTypeValidation.valid) {
    errors.matchType = matchTypeValidation.error
  }
  
  const focusValidation = validateRequired(formData.focus, 'Focus')
  if (!focusValidation.valid) {
    errors.focus = focusValidation.error
  }
  
  // Validate plan selection
  const planValidation = validateRequired(formData.planId, 'Package')
  if (!planValidation.valid) {
    errors.planId = planValidation.error
  }
  
  // Validate video hours (can be string or number)
  const videoHoursValue = formData.videoHours
  if (!videoHoursValue || (typeof videoHoursValue === 'string' && videoHoursValue.trim() === '')) {
    errors.videoHours = 'Video duration is required'
  } else {
    const hours = typeof videoHoursValue === 'number' ? videoHoursValue : parseFloat(videoHoursValue)
    if (isNaN(hours) || hours <= 0) {
      errors.videoHours = 'Please enter a valid duration (minimum 0.5 hours)'
    } else if (hours > 24) {
      errors.videoHours = 'Video duration cannot exceed 24 hours'
    }
  }
  
  return {
    valid: Object.keys(errors).length === 0,
    errors,
  }
}
